<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import CampaignsAPI from 'dashboard/api/campaigns';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const props = defineProps({
  wizardData: { type: Object, required: true },
});

const emit = defineEmits(['update']);
const { t } = useI18n();

const LABEL_REGEX = /^[\p{L}\p{N}]+[\p{L}\p{N}_-]+$/u;

const isUploading = ref(false);
const dragOver = ref(false);
const fileInput = ref(null);
const autoLabelName = ref('');
const labelSearchQuery = ref('');
const showLabelError = ref(false);
const showFileError = ref(false);

const csvSampleUrl = '/downloads/campaign-import-sample.csv';

const labels = useMapGetter('labels/getLabels');

const labelOptions = computed(() => {
  const existing = (labels.value || []).map(l => ({
    value: l.title,
    label: l.title,
  }));

  const query = labelSearchQuery.value.toLowerCase().trim();
  if (!query) return existing;

  const filtered = existing.filter(o => o.value.toLowerCase().includes(query));
  const exactMatch = filtered.some(o => o.value.toLowerCase() === query);
  if (exactMatch || !LABEL_REGEX.test(query)) return filtered;

  return [
    {
      value: query,
      label: `${t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.AUTO_LABEL.CREATE')}: ${query}`,
    },
    ...filtered,
  ];
});

const onLabelSelect = value => {
  autoLabelName.value = value;
  showLabelError.value = false;
  emit('update', { autoLabel: value || null });
};

const onLabelSearch = query => {
  labelSearchQuery.value = query;
};

const excludeOptions = computed(
  () => labels.value?.map(l => ({ value: l.id, label: l.title })) ?? []
);

const updateExcludedLabels = value => {
  emit('update', { excludedLabels: value });
};

const validate = () => {
  let valid = true;

  if (!props.wizardData.importId) {
    showFileError.value = true;
    valid = false;
  }

  if (!autoLabelName.value) {
    showLabelError.value = true;
    valid = false;
  }

  return valid;
};

defineExpose({ validate });

const handleFile = async file => {
  if (!file) return;

  const validTypes = ['text/csv', 'application/vnd.ms-excel'];
  if (!validTypes.includes(file.type) && !file.name.endsWith('.csv')) {
    useAlert(t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.INVALID_FILE'));
    return;
  }

  isUploading.value = true;
  try {
    const formData = new FormData();
    formData.append('import_file', file);

    const { data } = await CampaignsAPI.previewImport(formData);

    showFileError.value = false;
    emit('update', {
      importId: data.import_id,
      csvHeaders: data.headers,
      csvSampleRows: data.sample_rows,
      csvTotalRows: data.total_rows,
    });

    useAlert(
      t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.SUCCESS', {
        count: data.total_rows,
      })
    );
  } catch (error) {
    // Clear stale import data on error
    emit('update', {
      importId: null,
      csvHeaders: [],
      csvSampleRows: [],
      csvTotalRows: 0,
    });

    const errorMsg =
      error?.response?.data?.error ||
      t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.ERROR');
    useAlert(errorMsg);
  } finally {
    isUploading.value = false;
    // Reset file input to allow re-selecting the same file
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
};

const onDrop = event => {
  dragOver.value = false;
  const file = event.dataTransfer?.files?.[0];
  handleFile(file);
};

const onFileSelect = event => {
  const file = event.target?.files?.[0];
  handleFile(file);
};

const openFilePicker = () => {
  fileInput.value?.click();
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.TITLE') }}
    </h3>

    <!-- Drop zone -->
    <div
      class="flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed rounded-lg transition-colors cursor-pointer"
      :class="[
        dragOver
          ? 'border-n-brand bg-n-brand/5'
          : showFileError
            ? 'border-n-ruby-8 hover:border-n-ruby-9'
            : 'border-n-weak hover:border-n-slate-6',
      ]"
      @dragover.prevent="dragOver = true"
      @dragleave="dragOver = false"
      @drop.prevent="onDrop"
      @click="openFilePicker"
    >
      <span
        v-if="isUploading"
        class="i-lucide-loader-2 size-8 text-n-brand animate-spin"
      />
      <span v-else class="i-lucide-upload size-8 text-n-slate-10" />
      <div class="text-center">
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.DROP_TEXT') }}
        </p>
        <p class="text-xs text-n-slate-10 mt-1">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.FORMAT_HINT') }}
        </p>
      </div>
      <input
        ref="fileInput"
        type="file"
        accept=".csv"
        class="hidden"
        @change="onFileSelect"
      />
    </div>
    <p v-if="showFileError" class="text-xs text-n-ruby-9 -mt-2">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.FILE_REQUIRED') }}
    </p>

    <!-- Format guide callout + sample download -->
    <div
      class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak bg-n-alpha-2"
    >
      <div class="flex items-center gap-2">
        <span class="i-lucide-info size-4 text-n-slate-11" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.FORMAT_GUIDE.TITLE') }}
        </span>
      </div>
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.FORMAT_GUIDE.PATTERN') }}
      </p>
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.FORMAT_GUIDE.EXAMPLE') }}
      </p>
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.FORMAT_GUIDE.PLUS_NOTE') }}
      </p>
      <a
        :href="csvSampleUrl"
        target="_blank"
        rel="noopener noreferrer"
        download="campaign-import-sample.csv"
        class="inline-flex items-center gap-1 text-xs text-n-blue-11"
      >
        <span class="i-lucide-download size-3.5" />
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.SAMPLE_DOWNLOAD') }}
      </a>
    </div>

    <!-- Label selector -->
    <div v-if="wizardData.csvHeaders.length > 0" class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <span class="i-lucide-tag size-4 text-n-slate-11" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.AUTO_LABEL.TITLE') }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.AUTO_LABEL.REQUIRED') }}
        </span>
      </div>
      <ComboBox
        :model-value="autoLabelName"
        :options="labelOptions"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.AUTO_LABEL.PLACEHOLDER')
        "
        :search-placeholder="
          t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.AUTO_LABEL.SEARCH')
        "
        :empty-state="t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.AUTO_LABEL.EMPTY')"
        :has-error="showLabelError"
        :message="
          showLabelError
            ? t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.AUTO_LABEL.ERROR')
            : t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.AUTO_LABEL.DESCRIPTION')
        "
        use-api-results
        @update:model-value="onLabelSelect"
        @search="onLabelSearch"
      />
    </div>

    <!-- Exclusion labels -->
    <div v-if="wizardData.csvHeaders.length > 0" class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.EXCLUDE_LABEL') }}
      </label>
      <TagMultiSelectComboBox
        :model-value="wizardData.excludedLabels"
        :options="excludeOptions"
        :label="t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.EXCLUDE_LABEL')"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.EXCLUDE_PLACEHOLDER')
        "
        class="[&>div>button]:bg-n-alpha-black2"
        @update:model-value="updateExcludedLabels"
      />
    </div>

    <!-- Preview table -->
    <div v-if="wizardData.csvHeaders.length > 0" class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.PREVIEW') }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{ wizardData.csvTotalRows }}
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.CSV_UPLOAD.ROWS') }}
        </span>
      </div>
      <div class="overflow-x-auto rounded-lg border border-n-weak">
        <table class="w-full text-xs">
          <thead>
            <tr class="bg-n-alpha-2">
              <th
                v-for="header in wizardData.csvHeaders"
                :key="header"
                class="px-3 py-2 text-left font-medium text-n-slate-11"
              >
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in wizardData.csvSampleRows"
              :key="idx"
              class="border-t border-n-weak"
            >
              <td
                v-for="header in wizardData.csvHeaders"
                :key="header"
                class="px-3 py-2 text-n-slate-12 truncate max-w-[200px]"
              >
                {{ row[header] || '-' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
